import {AppUrls} from 'shared/config/model/urls';

import React from 'react';
import {Link} from 'react-router-dom';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import {MonitorRowStatusCell, MonitorTagsModal} from 'entities/monitor';
import {ExceededModal} from 'entities/user';
import IServerOutput from 'features/server/model/Server/IServer';
import {useServerRow} from 'features/server/model/useServerRow';
import useUpdateServerTags from 'features/server/model/useUpdateServerTags';
import {getFormattedDate} from 'shared/lib/datetime';
import {formatBytesToGb} from 'features/server/lib/serverMetricsHelper';
import Edit from 'shared/ui/icons/General/Edit';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import RowTags from 'shared/ui/Table/RowTags/RowTags';

import ServerStatusBadge from '../ServerStatusBadge/ServerStatusBadge';
import ServerMenu from '../ServerMenu';
import ServerModal from '../ServerModal';
import ServerPauseModal from '../ServerPauseModal/ServerPauseModal';
import ServerResumeModal from '../ServerResumeModal/ServerResumeModal';
import ServerRegenerateTokenModal from '../ServerRegenerateTokenModal/ServerRegenerateTokenModal';
import ServerSetTokenModal from '../ServerSetTokenModal/ServerSetTokenModal';
import ServerDeleteModal from '../ServerDeleteModal/ServerDeleteModal';

import styles from './ServerRow.module.scss';

interface IServerRowProps {
    item: IServerOutput,
    canCreate: boolean,
    hasSelectedItems: boolean,
    isMassOperationsMode: boolean,
}

const ServerRow: React.FC<IServerRowProps> = ({item, canCreate, hasSelectedItems, isMassOperationsMode}) => {
    const {
        activeModal,
        closeModal: handleCloseModal,
        isSelected,
        isPaused,
        iconState,
        severity,
        dateFormat,
        tags,
        onCheckboxChange,
        onTagClick,
        onToggleClick,
        onDetailsClick,
        onEditClick,
        onTagsClick,
        onPauseClick,
        onResumeClick,
        onSetTokenClick,
        onRegenerateTokenClick,
        onDeleteClick,
    } = useServerRow(item);

    return (
        <>
            <tr
                className={cn(styles.clickable, severity && styles[severity])}
                onClick={onDetailsClick}
            >
                <MonitorRowStatusCell
                    isMassOperationsMode={isMassOperationsMode}
                    hasSelectedItems={hasSelectedItems}
                    isSelected={isSelected}
                    onCheckboxChange={onCheckboxChange}
                    isPaused={iconState.isPaused}
                    ok={iconState.ok}
                />
                <td className={styles.blockName}>
                    <div className={styles.containerName}>
                        <span className={styles.name} title={item.name}>{item.name}</span>
                        <span className={styles.editIcon} onClick={onEditClick}>
                            <Edit/>
                        </span>
                    </div>
                    {severity && (
                        <Link
                            className={styles.openIncidents}
                            to={`${AppUrls.SERVER_INCIDENTS}?server.uuid=${item.uuid}&exists[closed_at]=false`}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {t`Есть открытые инциденты`}
                        </Link>
                    )}
                    {tags.length > 0 && (
                        <RowTags tags={tags} onTagClick={onTagClick} disabled={hasSelectedItems}/>
                    )}
                </td>
                <td className={styles.statusCell} onClick={(e) => e.stopPropagation()}>
                    <ServerStatusBadge status={item.status}/>
                </td>
                <td>{item.hostname ?? t`нет данных`}</td>
                <td>{item.os ?? '—'}</td>
                <td>{item.agent_version ?? '—'}</td>
                <td>{item.cpu_cores ?? '—'}</td>
                <td>{formatBytesToGb(item.mem_total)}</td>
                <td>{formatBytesToGb(item.swap_total)}</td>
                <td className={styles.dateCell}>
                    {getFormattedDate(item.last_seen_at, {
                        format: dateFormat,
                        nullValue: '—',
                    })}
                </td>
                <td>{t`${item.retention_days} дн.`}</td>
                <td className={styles.toggleCell}>
                    <StatusToggle
                        condition={!isPaused}
                        onClick={onToggleClick}
                        notActiveText={t`На паузе`}
                        preventToggle={isPaused && !canCreate}
                    />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-center">
                        <ServerMenu
                            item={item}
                            onEditClick={onEditClick}
                            onTagsClick={onTagsClick}
                            onDetailsClick={onDetailsClick}
                            onPauseClick={onPauseClick}
                            onResumeClick={onResumeClick}
                            onSetTokenClick={onSetTokenClick}
                            onRegenerateTokenClick={onRegenerateTokenClick}
                            onDeleteClick={onDeleteClick}
                        />
                    </div>
                </td>
            </tr>

            <ServerModal
                item={item}
                open={activeModal === 'edit'}
                handleClose={handleCloseModal}
            />

            <MonitorTagsModal
                uuid={item.uuid}
                tags={item.tags}
                useUpdateTags={useUpdateServerTags}
                open={activeModal === 'tags'}
                handleClose={handleCloseModal}
            />

            <ServerPauseModal
                item={item}
                open={activeModal === 'pause'}
                handleClose={handleCloseModal}
            />

            <ServerResumeModal
                item={item}
                open={activeModal === 'resume'}
                handleClose={handleCloseModal}
            />

            <ServerSetTokenModal
                item={item}
                open={activeModal === 'setToken'}
                handleClose={handleCloseModal}
            />

            <ServerRegenerateTokenModal
                item={item}
                open={activeModal === 'regenerate'}
                handleClose={handleCloseModal}
            />

            <ServerDeleteModal
                item={item}
                open={activeModal === 'delete'}
                handleClose={handleCloseModal}
            />

            <ExceededModal
                isOpen={activeModal === 'limit'}
                handleClose={handleCloseModal}
                type="servers"
            />
        </>
    );
};

export default ServerRow;
